import React, { useCallback, useEffect, useMemo, useState } from "react";
import useProductImpressionTracker from "@danishagro/shared/src/gtm/useProductImpressionTracker";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import { DA_NewProductSimple } from "@danishagro/shared/src/interfaces/productv2.interface";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { useProductPrices } from "@danishagro/shared/src/hooks/useProductPrices.hook";
import { DA_ProductPrice } from "@danishagro/shared/src/interfaces/price.interface";
import { useFavorites } from "@danishagro/shared/src/contexts/favorites.context";
import { ProductListItem } from "@molecules/ProductListItem";
import S from "./RelewiseProductList.module.scss";

export interface RelewiseProductListProps {
    products: DA_NewProductSimple[];
    categoryId: string;
    onPricesFetched?: (prices: DA_ProductPrice[]) => void;
}

export const RelewiseProductList = ({
    products,
    categoryId,
    onPricesFetched,
}: RelewiseProductListProps) => {
    const { showPrices, displayFavoritesList } = useAppData();
    const [priceData, setPriceData] = useState<DA_ProductPrice[]>([]);
    const prices = useProductPrices(products);

    // Get favorites context
    let favorites;
    try {
        const favoritesContext = useFavorites();
        favorites = displayFavoritesList ? favoritesContext.favorites : undefined;
    } catch (error) {
        favorites = undefined;
    }

    // Build a mapping from productNumber to price for robust lookup
    const priceMap = useMemo(() => {
        const map = {};
        if (Array.isArray(priceData)) {
            priceData.forEach((p) => {
                if (p && p.productNumber) map[p.productNumber] = p;
            });
        }
        return map;
    }, [priceData]);

    useEffect(() => {
        if (showPrices && products && prices) {
            setPriceData(prices);
            onPricesFetched?.(prices);
        }
    }, [onPricesFetched, prices, products, showPrices]);

    // GTM Tracking
    const productRefs = useProductImpressionTracker(products);

    const formatPrice = useCallback(
        (price: number) => formatNumber(price, "en-GB", { decimals: 2 }),
        []
    );

    return (
        <div className={S.container}>
            {products.map((item, index) => {
                // Transforming the category URL
                const category = item?.url?.split("/").slice(0, -1).join("_").replace(/^_/, "");
                // Determine if the product is a favorite
                const isFavorite = favorites?.has(item.variantEntityId) ?? false;

                // Always look up price by productNumber
                const price = showPrices && item.productNumber && priceMap[item.productNumber];

                return (
                    <ProductListItem
                        ref={(el) => (productRefs.current[index] = el)}
                        categoryId={categoryId}
                        data-id={item?.masterProductNumber}
                        data-name={item?.displayName}
                        data-category={category}
                        eagerLoading={index < 8}
                        data-listid={item?.entityId} // TODO: figure out listId
                        data-price={formatPrice(item?.price?.priceWithoutVat)}
                        key={`${item?.entityId}--${index}`}
                        {...item}
                        price={price}
                        hasDiscount={price?.hasDiscount ?? item.hasDiscount}
                        isFavorite={isFavorite}
                        version="v1"
                        fetchPriority={index < 8}
                    />
                );
            })}
        </div>
    );
};
